/**
 * Goal 2: export reports Excel as one dialog-owned operation.
 * Dialog owns feedback — no toast.promise / axiosWithToast on export.
 */
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  canConfirmStepFromEvidence,
} from "../../../../operationFeedback";
import { excelReports } from "../../../../api/service/report";

export const EXPORT_REPORTS_EXCEL_OPERATION_KEY = "export_reports_excel";

export function evaluateExportReportsExcelPreflight(controller) {
  if (!controller || typeof controller !== "object") {
    return { ready: false, reason: "missing_controller" };
  }
  return { ready: true, reason: null };
}

function defaultExecutionSteps(t) {
  return [
    {
      key: "preflight",
      label: t("operation_feedback.export_reports_excel.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "export",
      label: t("operation_feedback.export_reports_excel.step.export"),
      status: STEP_STATUS.PENDING,
    },
  ];
}

/**
 * @param {object} args
 * @param {object} args.controller - list filters/params for excel endpoint
 * @param {string|number} [args.companyId]
 * @param {Function} [args.onExported] - called with { excelUrl, payload }
 * @param {Function} args.t
 * @param {Function} [args.exportFn]
 */
export async function runExportReportsExcelOperation({
  controller,
  companyId,
  onExported,
  t,
  exportFn = excelReports,
}) {
  const coordinator = getOperationCoordinator();
  const title = t("operation_feedback.export_reports_excel.title");

  const op = coordinator.start({
    operationKey: EXPORT_REPORTS_EXCEL_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId: null,
    companyId,
    steps: defaultExecutionSteps(t),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const preflight = evaluateExportReportsExcelPreflight(controller);
  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional */
  }

  if (!preflight.ready) {
    const detail = t("operation_feedback.export_reports_excel.preflight_failed");
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "export", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.NOT_APPLIED,
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.export_reports_excel.preflight_ok"),
    revision: rev++,
  });

  coordinator.updateStep(id, "export", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let response;
  try {
    response = await exportFn(controller);
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: EXPORT_REPORTS_EXCEL_OPERATION_KEY,
    });
    coordinator.updateStep(id, "export", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = response?.data ?? response;
  const excelUrl = payload?.excel_url || payload?.url || null;
  if (!excelUrl && !(payload instanceof Blob)) {
    const normalized = normalizeError(
      {
        response: {
          status: response?.status || 422,
          data: payload || { errors: ["missing excel_url"] },
        },
      },
      { operationKey: EXPORT_REPORTS_EXCEL_OPERATION_KEY }
    );
    coordinator.updateStep(id, "export", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: t("operation_feedback.export_reports_excel.export_failed"),
      outcome: OUTCOME.NOT_APPLIED,
      error: {
        safeMessage: normalized.safeMessage,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  coordinator.updateStep(id, "export", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.export_reports_excel.export_ok"),
    resultRef: excelUrl ? { excel_url: excelUrl } : undefined,
    revision: rev++,
  });

  if (typeof onExported === "function") {
    try {
      onExported({ excelUrl, payload, response });
    } catch (cbErr) {
      console.error(cbErr);
    }
  }

  const links = excelUrl
    ? [
        {
          href: excelUrl,
          label: t("operation_feedback.export_reports_excel.download"),
        },
      ]
    : [];

  coordinator.complete(id, {
    summary: t("operation_feedback.export_reports_excel.success"),
    outcome: OUTCOME.APPLIED,
    links,
  });
  return {
    clientOperationId: id,
    ok: true,
    payload,
    excelUrl,
  };
}
